import React, { useCallback, useEffect, useState } from 'react'
import {
  ArrowPathIcon,
  TrashIcon,
  ExclamationTriangleIcon,
  ShieldCheckIcon,
} from '@heroicons/react/24/outline'
import { getLocalApiUrl, authFetch } from '../../config/api'
import { ID_LABELS } from '../../utils/patronIdentity'
import DailyDataNav from './DailyDataNav'
import { formatDateTime } from './dailyDataUtils'

function PurgeConfirmModal({ open, preview, loading, onClose, onConfirm }) {
  if (!open) return null
  const count = preview?.would_delete ?? preview?.patrons?.length ?? 0
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
        <div className="flex items-start gap-3">
          <ExclamationTriangleIcon className="w-6 h-6 text-amber-600 shrink-0" />
          <div>
            <h3 className="text-lg font-semibold text-gray-900">Confirm permanent delete</h3>
            <p className="text-sm text-gray-600 mt-2">
              This will permanently remove <strong>{count}</strong> DimPatron-imported patron
              {count === 1 ? '' : 's'} and any matching legacy directory rows.
              This cannot be undone.
            </p>
            <p className="text-xs text-gray-500 mt-2">
              Only patrons with no investigations, payouts, watchlist, KYC, ECDD, or alerts are included.
            </p>
          </div>
        </div>
        <div className="mt-6 flex justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            disabled={loading}
            className="px-4 py-2 text-sm border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={onConfirm}
            disabled={loading || count < 1}
            className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-md hover:bg-red-700 disabled:opacity-50"
          >
            {loading ? 'Deleting…' : `Delete ${count} patron${count === 1 ? '' : 's'}`}
          </button>
        </div>
      </div>
    </div>
  )
}

export default function DataManagement() {
  const [summary, setSummary] = useState(null)
  const [candidates, setCandidates] = useState([])
  const [pagination, setPagination] = useState({ page: 1, page_size: 50, total: 0 })
  const [filter, setFilter] = useState('deletable')
  const [loading, setLoading] = useState(true)
  const [purging, setPurging] = useState(false)
  const [message, setMessage] = useState(null)
  const [confirmOpen, setConfirmOpen] = useState(false)
  const [purgePreview, setPurgePreview] = useState(null)
  const [purgeMemberIds, setPurgeMemberIds] = useState([])
  const [selectedIds, setSelectedIds] = useState(new Set())

  const loadSummary = useCallback(async () => {
    const res = await authFetch(getLocalApiUrl('/api/daily-data/manage/patrons/summary'))
    const json = await res.json()
    if (!json.success) throw new Error(json.error || 'Failed to load summary')
    setSummary(json.data)
  }, [])

  const loadCandidates = useCallback(async (page = 1, deletableOnly = filter === 'deletable') => {
    const params = new URLSearchParams({
      page: String(page),
      page_size: '50',
      deletable_only: deletableOnly ? 'true' : 'false',
    })
    const res = await authFetch(getLocalApiUrl(`/api/daily-data/manage/patrons/candidates?${params}`))
    const json = await res.json()
    if (!json.success) throw new Error(json.error || 'Failed to load patrons')
    setCandidates(json.data.items || [])
    setPagination(json.data.pagination || { page: 1, page_size: 50, total: 0 })
    setSelectedIds(new Set())
  }, [filter])

  const loadAll = useCallback(async () => {
    setLoading(true)
    setMessage(null)
    try {
      await Promise.all([loadSummary(), loadCandidates(1)])
    } catch (e) {
      setMessage({ type: 'error', text: e.message })
    } finally {
      setLoading(false)
    }
  }, [loadSummary, loadCandidates])

  useEffect(() => {
    loadAll()
  }, [loadAll])

  useEffect(() => {
    if (!loading) {
      loadCandidates(1).catch((e) => setMessage({ type: 'error', text: e.message }))
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter])

  const toggleSelect = (memberId) => {
    if (!memberId) return
    setSelectedIds((prev) => {
      const next = new Set(prev)
      if (next.has(memberId)) next.delete(memberId)
      else next.add(memberId)
      return next
    })
  }

  const selectAllDeletable = () => {
    const ids = candidates.filter((c) => c.deletable && c.member_id).map((c) => c.member_id)
    setSelectedIds(new Set(ids))
  }

  const runPurgePreview = async ({ memberIds = null, limit = 500 } = {}) => {
    setPurging(true)
    setMessage(null)
    try {
      const body = { dry_run: true, limit }
      if (memberIds?.length) body.member_ids = memberIds
      const res = await authFetch(getLocalApiUrl('/api/daily-data/manage/patrons/purge'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      })
      const json = await res.json()
      if (!json.success) throw new Error(json.error || 'Preview failed')
      const ids = (json.data.patrons || []).map((p) => p.member_id).filter(Boolean)
      if (!ids.length) {
        setMessage({ type: 'error', text: 'No eligible patrons to remove.' })
        return
      }
      setPurgePreview(json.data)
      setPurgeMemberIds(ids)
      setConfirmOpen(true)
    } catch (e) {
      setMessage({ type: 'error', text: e.message })
    } finally {
      setPurging(false)
    }
  }

  const runPurgeExecute = async () => {
    setPurging(true)
    try {
      const body = {
        dry_run: false,
        limit: 500,
        member_ids: purgeMemberIds,
      }
      const res = await authFetch(getLocalApiUrl('/api/daily-data/manage/patrons/purge'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      })
      const json = await res.json()
      if (!json.success) throw new Error(json.error || 'Delete failed')
      setConfirmOpen(false)
      setPurgePreview(null)
      setMessage({
        type: 'success',
        text: `Deleted ${json.data.deleted} patron profile(s)${json.data.member_directory_deleted ? ` and ${json.data.member_directory_deleted} legacy directory row(s)` : ''}.`,
      })
      await loadAll()
    } catch (e) {
      setMessage({ type: 'error', text: e.message })
    } finally {
      setPurging(false)
    }
  }

  const totalPages = Math.max(1, Math.ceil((pagination.total || 0) / (pagination.page_size || 50)))

  if (loading && !summary) {
    return (
      <div className="flex items-center justify-center min-h-96">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600" />
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <DailyDataNav />

      <div className="bg-white shadow rounded-lg p-6">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Manage data</h1>
            <p className="text-gray-600 mt-1">
              Remove unused patrons imported from DimPatron when they have no compliance or operational links.
            </p>
          </div>
          <button
            type="button"
            onClick={loadAll}
            className="inline-flex items-center px-3 py-2 border rounded-md text-sm hover:bg-gray-50"
          >
            <ArrowPathIcon className="w-4 h-4 mr-1" />
            Refresh
          </button>
        </div>
      </div>

      {message && (
        <div
          className={`rounded-lg border p-4 text-sm ${
            message.type === 'error'
              ? 'bg-red-50 border-red-200 text-red-800'
              : 'bg-green-50 border-green-200 text-green-800'
          }`}
        >
          {message.text}
        </div>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div className="rounded-lg border bg-white p-4 shadow-sm">
          <p className="text-xs font-medium text-gray-500 uppercase">DimPatron imports</p>
          <p className="text-2xl font-bold text-gray-900 mt-1">{summary?.total ?? '—'}</p>
        </div>
        <div className="rounded-lg border border-green-200 bg-green-50 p-4 shadow-sm">
          <p className="text-xs font-medium text-green-700 uppercase flex items-center gap-1">
            <ShieldCheckIcon className="w-4 h-4" />
            Safe to remove
          </p>
          <p className="text-2xl font-bold text-green-900 mt-1">{summary?.deletable ?? '—'}</p>
        </div>
        <div className="rounded-lg border border-amber-200 bg-amber-50 p-4 shadow-sm">
          <p className="text-xs font-medium text-amber-800 uppercase">Linked — keep</p>
          <p className="text-2xl font-bold text-amber-900 mt-1">{summary?.blocked ?? '—'}</p>
        </div>
      </div>

      <div className="bg-white shadow rounded-lg overflow-hidden">
        <div className="px-4 py-3 border-b border-gray-200 flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-2">
            <span className="text-sm text-gray-600">Show:</span>
            <select
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              className="text-sm border border-gray-300 rounded-md px-2 py-1.5"
            >
              <option value="deletable">Safe to remove only</option>
              <option value="all">All DimPatron imports</option>
            </select>
          </div>
          <div className="flex flex-wrap gap-2">
            {filter === 'deletable' && candidates.length > 0 && (
              <button
                type="button"
                onClick={selectAllDeletable}
                className="text-sm text-primary-700 hover:underline"
              >
                Select all on page
              </button>
            )}
            <button
              type="button"
              disabled={purging || (summary?.deletable ?? 0) < 1}
              onClick={() => runPurgePreview({ memberIds: selectedIds.size ? [...selectedIds] : null })}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-white bg-red-600 rounded-md hover:bg-red-700 disabled:opacity-50"
            >
              <TrashIcon className="w-4 h-4" />
              {selectedIds.size > 0
                ? `Remove selected (${selectedIds.size})`
                : `Remove all eligible (up to 500)`}
            </button>
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead className="bg-gray-50 text-left text-xs font-medium text-gray-500 uppercase">
              <tr>
                <th className="px-4 py-2 w-10" />
                <th className="px-4 py-2">Patron</th>
                <th className="px-4 py-2">{ID_LABELS.player}</th>
                <th className="px-4 py-2">{ID_LABELS.membership}</th>
                <th className="px-4 py-2">Last import</th>
                <th className="px-4 py-2">Status</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {candidates.length === 0 ? (
                <tr>
                  <td colSpan={6} className="px-4 py-10 text-center text-gray-500">
                    {filter === 'deletable'
                      ? 'No unused DimPatron imports — all imported patrons are linked to cases, payouts, or other records.'
                      : 'No DimPatron-imported patrons found.'}
                  </td>
                </tr>
              ) : (
                candidates.map((row) => (
                  <tr key={row.master_profile_id} className={row.deletable ? '' : 'bg-gray-50/50'}>
                    <td className="px-4 py-2">
                      {row.deletable && row.member_id && (
                        <input
                          type="checkbox"
                          checked={selectedIds.has(row.member_id)}
                          onChange={() => toggleSelect(row.member_id)}
                          className="rounded border-gray-300"
                        />
                      )}
                    </td>
                    <td className="px-4 py-2 font-medium text-gray-900">{row.display_name}</td>
                    <td className="px-4 py-2 font-mono text-xs text-gray-600">{row.member_id || '—'}</td>
                    <td className="px-4 py-2 font-mono text-xs text-gray-600">{row.membership_number || '—'}</td>
                    <td className="px-4 py-2 text-gray-600">{formatDateTime(row.last_import_at)}</td>
                    <td className="px-4 py-2">
                      {row.deletable ? (
                        <span className="inline-flex px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                          Safe to remove
                        </span>
                      ) : (
                        <div className="flex flex-wrap gap-1">
                          {(row.blockers || []).map((b) => (
                            <span
                              key={b.code}
                              className="inline-flex px-2 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-900"
                              title={b.label}
                            >
                              {b.label}
                            </span>
                          ))}
                        </div>
                      )}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {pagination.total > pagination.page_size && (
          <div className="px-4 py-3 border-t border-gray-200 flex items-center justify-between text-sm text-gray-600">
            <span>
              Page {pagination.page} of {totalPages} ({pagination.total} total)
            </span>
            <div className="flex gap-2">
              <button
                type="button"
                disabled={pagination.page <= 1}
                onClick={() => loadCandidates(pagination.page - 1)}
                className="px-3 py-1 border rounded-md disabled:opacity-40"
              >
                Previous
              </button>
              <button
                type="button"
                disabled={pagination.page >= totalPages}
                onClick={() => loadCandidates(pagination.page + 1)}
                className="px-3 py-1 border rounded-md disabled:opacity-40"
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>

      <p className="text-xs text-gray-500">
        Only patrons with <code className="bg-gray-100 px-1 rounded">created_touchpoint = DIM_PATRON_IMPORT</code> are
        considered. Patrons from payouts, onboarding, or KYC are never removed by this tool.
      </p>

      <PurgeConfirmModal
        open={confirmOpen}
        preview={purgePreview}
        loading={purging}
        onClose={() => !purging && setConfirmOpen(false)}
        onConfirm={runPurgeExecute}
      />
    </div>
  )
}
